import { useAuthStore } from '@features/auth/store/authStore';
import axios, { AxiosRequestConfig, Method } from 'axios';
import { ErrorResponse } from '@core/types/errorResponse';
import ApiResponse from '@core/types/apiResponse';
import { sendRequest as sendRequestUtils } from '@infrastructure/api/utils/requestUtils';
import { clearClientSessionArtifacts } from '@features/auth/utils/clearClientSessionArtifacts';

const api = axios.create({
  baseURL: `${process.env.REACT_APP_API_BASE_URL}`,
});

let refreshTokenInFlight: Promise<string | null> | null = null;
const inFlightGetRequests = new Map<string, Promise<ApiResponse<any, ErrorResponse>>>();

function buildGetRequestKey(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig
) {
  return JSON.stringify({
    url,
    data: data ?? null,
    params: config?.params ?? null,
  });
}

/** Clear local session caches but keep return URL so the user can resume after re-login. */
function clearSessionOnRefreshFailure(): void {
  useAuthStore.getState().clearAccessToken();
  clearClientSessionArtifacts({ clearReturnUrl: false });
}

const getValidAccessTokenAsync = async (): Promise<string | null> => {
  let accessToken = useAuthStore.getState().getAccessToken();

  // Check if token is expired or will expire soon (within 5 minutes)
  if (!accessToken || !useAuthStore.getState().isAuthenticated()) {
    try {
      if (!refreshTokenInFlight) {
        refreshTokenInFlight = import('@features/auth/services/loginSvc')
          .then(({ refreshTokenAsync }) => refreshTokenAsync())
          .then((response) => {
            if (response.isSuccess()) {
              return response.getData()!.token;
            }

            return null;
          })
          .catch(() => null)
          .finally(() => {
            refreshTokenInFlight = null;
          });
      }

      accessToken = await refreshTokenInFlight;
      if (!accessToken) {
        clearSessionOnRefreshFailure();
        return null;
      }
    } catch {
      clearSessionOnRefreshFailure();
      return null;
    }
  }

  return accessToken;
};

api.interceptors.request.use(
  async (config) => {
    config.withCredentials = true;

    const accessToken = await getValidAccessTokenAsync();
    if (accessToken) {
      config.headers['Authorization'] = `Bearer ${accessToken}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);



export async function sendRequest<TReq, TRes>(
  method: Method,
  url: string,
  data?: TReq,
  config?: AxiosRequestConfig
): Promise<ApiResponse<TRes, ErrorResponse>> {
  const normalizedMethod = method.toUpperCase();

  if (normalizedMethod === 'GET' && !config?.signal) {
    const requestKey = buildGetRequestKey(url, data, config);

    if (!inFlightGetRequests.has(requestKey)) {
      inFlightGetRequests.set(
        requestKey,
        sendRequestUtils(api, method, url, data, config).finally(() => {
          inFlightGetRequests.delete(requestKey);
        })
      );
    }

    return inFlightGetRequests.get(requestKey)! as Promise<ApiResponse<TRes, ErrorResponse>>;
  }

  return sendRequestUtils(api, method, url, data, config);
}
